import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Scissors, Maximize2 } from 'lucide-react';
import { ZONE_LABELS, CUT_ZONE_TEXT, EXTENSION_ZONE_TEXT, buildZoneHtml, isDefaultZoneHtml } from './CutExtensionZoneData';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';

/* ══════════════════════════════════════════════════════════════════
   Cut / Extension Zone Guide — auto-populated from the 16-Zone Bar
   Chart's own min/max reference lines (same formula the chart already
   draws): any direction whose measured area falls below the chart's
   min-line is a "Cut" zone, above the max-line is an "Extension" zone.
   Reuses the Vastu Devta Guide's editing pattern (rich text, per-zone
   toggle) — just no image, and the zone list is computed live instead
   of being a fixed 45-item dataset.
   ══════════════════════════════════════════════════════════════════ */

const RichEditor = React.memo(function RichEditor({ value, onChange, zoneKey }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || '<p><br></p>';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneKey]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const btn = (extra = {}) => ({
    border: '1px solid #e8eaed', borderRadius: 4, background: '#fff', color: '#3c4043',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 26, padding: '0 7px', fontSize: '0.74rem', fontFamily: 'inherit', ...extra,
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '5px 8px', background: '#fafafa', borderBottom: '1px solid #e8eaed', flexWrap: 'wrap' }}>
        <select onMouseDown={e => e.preventDefault()} onChange={e => { formatBlock(e.target.value); e.target.value = ''; }} defaultValue=""
          style={{ fontSize: '0.71rem', fontWeight: 600, border: '1px solid #e8eaed', borderRadius: 4, padding: '2px 6px', color: '#3c4043', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: 'inherit', height: 26 }}>
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h2">H2 — Heading</option>
          <option value="h3">H3 — Sub-heading</option>
        </select>
        <span style={{ width: 1, height: 16, background: '#e8eaed', margin: '0 2px' }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('bold'); }} style={btn({ fontWeight: 800, width: 26, padding: 0 })}>B</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('italic'); }} style={btn({ fontStyle: 'italic', width: 26, padding: 0 })}>I</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('underline'); }} style={btn({ textDecoration: 'underline', width: 26, padding: 0 })}>U</button>
        <span style={{ width: 1, height: 16, background: '#e8eaed', margin: '0 2px' }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} style={btn()}>1. List</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} style={btn()}>• List</button>
        <span style={{ flex: 1 }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={btn({ color: '#80868b', fontSize: '0.67rem' })}>Clear</button>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={handleInput}
        data-placeholder="Type here… use toolbar to format" className="ceg-editor"
        style={{ minHeight: 140, padding: '12px 14px', outline: 'none', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", fontSize: '0.84rem', color: '#202124', lineHeight: 1.65 }} />
    </div>
  );
});

const Toggle = ({ checked, onChange }) => (
  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer', flexShrink: 0 }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
    <span style={{ position: 'absolute', inset: 0, background: checked ? '#00A19D' : '#CBD5E1', borderRadius: 24, transition: 'background .2s' }} />
    <span style={{ position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20, background: '#fff', borderRadius: '50%', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.18)' }} />
  </label>
);

const ZoneCard = React.memo(function ZoneCard({ item, onToggle, onHtmlChange, language = 'en', isEdited = false }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${item.enabled ? '#00A19D' : '#e2e8f0'}`, boxShadow: item.enabled ? '0 1px 12px rgba(0,161,157,.08)' : '0 1px 3px rgba(0,0,0,.04)', overflow: 'hidden', transition: 'border-color .2s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: item.enabled ? '#f0fdfc' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: item.enabled ? '#00A19D' : '#e2e8f0', color: item.enabled ? '#fff' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700 }}>
          {item.zone}
        </div>
        <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: 700, color: item.enabled ? '#0f172a' : '#94a3b8' }}>{item.label}</span>
        <span style={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 600 }}>Area: {item.area}{item.unit}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          {isEdited && (
            <span
              title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
              style={{ fontSize: '0.62rem', color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}
            >
              Edited
            </span>
          )}
          <span style={{ fontSize: '0.68rem', color: item.enabled ? '#00857E' : '#94a3b8', fontWeight: 600 }}>{item.enabled ? 'ON' : 'OFF'}</span>
          <Toggle checked={item.enabled} onChange={onToggle} />
        </div>
      </div>
      <div style={{ padding: '12px 14px 14px' }}>
        <RichEditor zoneKey={`${item.zone}-${language}`} value={item.richHtml} onChange={onHtmlChange} />
      </div>
    </div>
  );
});

export default function CutExtensionGuide({ openModal = false, onModalClose = null, onReportGenerated = null, mode = 'cut', zones = [], projectId = null, language = 'en' }) {
  // NOTE: deliberately no local "showModal" state mirroring the openModal
  // prop — that duplicate-state + two-effect sync pattern raced against
  // itself (an unstable inline onModalClose reference from the parent made
  // the sync effect re-fire and close the modal a beat after it opened).
  // openModal is the single source of truth; closing just calls the prop.
  // Mobile: this modal's header (title+subtitle+badge+3 buttons) and the
  // fixed minmax(560px,...) card grid were desktop-only sizing — both
  // overflowed / never fit a phone viewport. Fixed below via isMobile-
  // gated styles only; desktop keeps every original style untouched.
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const STORAGE_KEY = `${mode === 'cut' ? 'cut_zone_data' : 'extension_zone_data'}_${projectId || 'new'}`;
  const isCut = mode === 'cut';
  const textData = isCut ? CUT_ZONE_TEXT : EXTENSION_ZONE_TEXT;
  const closeModal = () => { if (onModalClose) onModalClose(); };

  // Rebuild the list whenever the live flagged-zone set changes (e.g. the
  // polygon/calibration was redrawn) — preserving any edited richHtml/enabled
  // state a user already saved for a zone that's still flagged.
  useEffect(() => {
    let saved = [];
    try { const raw = localStorage.getItem(STORAGE_KEY); if (raw) saved = JSON.parse(raw); } catch {}
    const savedMap = {};
    saved.forEach(s => { savedMap[s.zone] = s; });

    setItems(zones.map(z => {
      const prev = savedMap[z.zone];
      const label = ZONE_LABELS[z.zone] || z.zone;
      const txt = textData[z.zone] || { symptom: '—', remedy: '—' };
      const defaultHtml = buildZoneHtml(label, mode, txt, language);
      // Keep a saved richHtml only if the user actually hand-edited it. A saved
      // default (in whatever language it was last saved) gets regenerated in
      // the currently selected language so the export reflects the dropdown.
      const keepSaved = prev?.richHtml && !isDefaultZoneHtml(label, mode, txt, prev.richHtml);
      return {
        zone: z.zone, label, area: z.area, unit: z.unit,
        enabled: prev ? prev.enabled !== false : true,
        richHtml: keepSaved ? prev.richHtml : defaultHtml,
      };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones, STORAGE_KEY, mode, language]);

  // Language switch: re-translate every zone whose text is still the auto-built
  // default (leave hand-edited ones alone), same pattern as the other report
  // editors (Vastu Devta Guide, Zone Analysis, Feng Shui).
  useEffect(() => {
    setItems(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(it => {
        const txt = textData[it.zone] || { symptom: '—', remedy: '—' };
        if (!isDefaultZoneHtml(it.label, mode, txt, it.richHtml)) return it;
        const translated = buildZoneHtml(it.label, mode, txt, language);
        if (translated === it.richHtml) return it;
        changed = true;
        return { ...it, richHtml: translated };
      });
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const handleToggle = (zone) => setItems(p => p.map(it => it.zone === zone ? { ...it, enabled: !it.enabled } : it));
  const handleHtml = useCallback((zone, html) => setItems(p => p.map(it => it.zone === zone ? { ...it, richHtml: html } : it)), []);

  const handleSave = () => {
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      setSavedMsg('All changes saved');
      setTimeout(() => setSavedMsg(''), 3000);
      // Let Configure auto-enable the export toggle, same as every other
      // report editor (Vastu Devta Guide, Navgrah Shanti, Feng Shui, Zone
      // Analysis) — without this the sidebar toggle stayed locked/off even
      // right after a successful save.
      if (onReportGenerated) onReportGenerated({ mode, reportData: items });
    } catch { setSavedMsg('Save failed'); }
    setIsSaving(false);
  };

  const leave = useLeaveConfirm({ onSave: () => { handleSave(); closeModal(); }, onLeave: closeModal });

  if (!openModal) return null;

  const enabledCount = items.filter(i => i.enabled).length;
  const Icon = isCut ? Scissors : Maximize2;
  const accent = isCut ? '#E4572E' : '#2B6CB0';

  return (
    /* z-index bumped above the mobile bottom toolbar (10100)/sheets — this
       used to render behind them on phone, looking like it never opened. */
    <div style={{ position: 'fixed', inset: 0, zIndex: 10500, display: 'flex', flexDirection: 'column', background: '#f1f5f9', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", textAlign: 'left' }}>
      <style>{`
        .ceg-editor h2 { font-size:0.9rem; font-weight:800; color:#0f172a; margin:4px 0 4px; }
        .ceg-editor h3 { font-size:0.8rem; font-weight:700; color:#334155; margin:8px 0 2px; }
        .ceg-editor p { font-size:0.82rem; color:#475569; margin:1px 0 6px; line-height:1.65; }
        .ceg-editor ul, .ceg-editor ol { font-size:0.82rem; color:#475569; padding-left:1.8em; margin:2px 0 6px; line-height:1.65; }
        .ceg-editor li { margin-bottom:2px; }
        .ceg-editor strong { font-weight:700; color:#1e293b; }
        .ceg-editor:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
        .ceg-editor:focus { outline:none; }
      `}</style>

      <div style={{ background: '#0f172a', color: '#fff', padding: isMobile ? '0 14px' : '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, minWidth: 0 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${accent}, ${accent}aa)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={16} color="#fff" /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.94rem', fontWeight: 700, lineHeight: 1.2 }}>{isCut ? 'Cut Zones' : 'Extension Zones'}</div>
            {!isMobile && <div style={{ fontSize: '0.68rem', color: '#64748b' }}>Auto-detected from 16-Zone Bar Chart {isCut ? 'below min-line' : 'above max-line'}</div>}
          </div>
          {!isMobile && (
            <>
              <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,.1)', margin: '0 4px' }} />
              <span style={{ fontSize: '0.72rem', background: 'rgba(0,161,157,.2)', color: '#4ffffa', border: '1px solid rgba(0,161,157,.35)', borderRadius: 20, padding: '2px 10px', fontWeight: 600 }}>{enabledCount} enabled</span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, flexShrink: 0 }}>
          {savedMsg && !isMobile && <span style={{ fontSize: '0.76rem', color: '#4ade80', fontWeight: 500 }}>✓ {savedMsg}</span>}
          {isMobile ? (
            <button onClick={handleSave} disabled={isSaving} title={isSaving ? 'Saving…' : 'Save Report'}
              style={{ background: '#00A19D', border: 'none', color: '#fff', borderRadius: 7, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isSaving ? .7 : 1, flexShrink: 0 }}>
              <Save size={14} />
            </button>
          ) : (
            <>
              <button onClick={handleSave} disabled={isSaving}
                style={{ background: '#00A19D', border: 'none', color: '#fff', borderRadius: 7, padding: '7px 18px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, opacity: isSaving ? .7 : 1 }}>
                <Save size={13} />{isSaving ? 'Saving…' : 'Save Report'}
              </button>
              <button onClick={() => { handleSave(); closeModal(); }}
                style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', color: '#fff', borderRadius: 7, padding: '7px 18px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Save size={13} />Save &amp; Close
              </button>
            </>
          )}
          <button onClick={leave.requestClose}
            style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.1)', color: '#94a3b8', borderRadius: 7, padding: isMobile ? '9px' : '7px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <X size={15} />
          </button>
          {leave.dialog}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 14px 30px' : '20px 24px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(560px,1fr))', gap: isMobile ? 12 : 18 }}>
          {items.map(item => (
            <ZoneCard key={item.zone} item={item} language={language} isEdited={!isDefaultZoneHtml(item.label, mode, textData[item.zone] || { symptom: '—', remedy: '—' }, item.richHtml)} onToggle={() => handleToggle(item.zone)} onHtmlChange={html => handleHtml(item.zone, html)} />
          ))}
        </div>
        {items.length === 0 && (
          <div style={{ padding: '60px 20px', color: '#94a3b8', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 10 }}>{isCut ? '✂️' : '📐'}</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 500 }}>
              No {isCut ? 'cut' : 'extension'} zones detected — every direction's area is within the normal min/max range.
            </div>
            <div style={{ fontSize: '0.76rem', marginTop: 4 }}>
              (Make sure the plot polygon, compass center, and calibration are set so the 16-Zone Bar Chart has data.)
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
